import type { DeviceApi } from '../data/backend';

/**
 * Live Khutbah, on the screen chosen to listen: hears the imam (the PC's microphone or a mixer plugged into it),
 * streams the sound straight to Soniox's speech-to-text with a short-lived key, and sends each finished sentence to
 * be translated. Words Soniox wasn't sure of are wrapped in ⟦ ⟧, so they're underlined as unclear on the screens.
 *
 * The sound goes as WebM/Opus from the browser's recorder (no audio processing on the page, so it works on a screen
 * nobody has touched). It reconnects by itself if the connection drops, with a new key each time.
 */

export type ListenStatus = 'starting' | 'listening' | 'no-mic' | 'mic-blocked' | 'offline' | 'stopped';

const URL_WS = 'wss://stt-rt.soniox.com/transcribe-websocket';
/** Below this confidence a word counts as not heard clearly. */
const UNSURE = 0.55;
/** A sentence is finished when Soniox hears a pause, at a full stop once it has a few words, or when it gets this long. */
const MAX_WORDS = 38, MIN_WORDS = 4;
/**
 * While it's spoken, the sentence so far goes to be translated each time this many more words are settled (or after a
 * short wait for fewer), one call at a time, so the English follows the Arabic a few words behind.
 */
const MORE_WORDS = 2, MORE_WAIT_MS = 500;

/** The sentence being spoken: its calls to be translated run one after another, the finished one last. */
interface Sentence { seq: number; chain: Promise<void>; queued: boolean; sent: number; finished: boolean; timer: ReturnType<typeof setTimeout> | null }

interface Token { text: string; start_ms?: number; end_ms?: number; confidence?: number; is_final?: boolean }

/** Words from Soniox's pieces (a piece starting with a space starts a new word), each with its lowest confidence. */
export function wordsFrom(tokens: readonly Token[]): Array<{ w: string; c: number }> {
  const out: Array<{ w: string; c: number }> = [];
  for (const t of tokens) {
    if (!t.text || t.text === '<end>' || t.text === '<fin>') continue;
    const c = typeof t.confidence === 'number' ? t.confidence : 1;
    const parts = t.text.split(/(\s+)/);
    for (const p of parts) {
      if (!p) continue;
      if (/^\s+$/.test(p)) { out.push({ w: '', c: 1 }); continue; }
      const last = out.at(-1);
      if (last && last.w) { last.w += p; last.c = Math.min(last.c, c); } else if (last && !last.w) { last.w = p; last.c = c; } else out.push({ w: p, c });
    }
  }
  return out.filter((x) => x.w);
}

/** The sentence as text, unsure words wrapped in ⟦ ⟧ (next to each other, they share one pair). */
export function markUnsure(words: ReadonlyArray<{ w: string; c: number }>): string {
  let out = '', open = false;
  words.forEach((x, i) => {
    const unsure = x.c < UNSURE && /[\p{L}\p{N}]/u.test(x.w);
    if (unsure && !open) { out += (i ? ' ' : '') + '⟦' + x.w; open = true; return; }
    if (!unsure && open) { out += '⟧'; open = false; }
    out += (i ? ' ' : '') + x.w;
  });
  if (open) out += '⟧';
  return out.replace(/⟦ /g, '⟦');
}

export class KhutbahListener {
  private ws: WebSocket | null = null;
  private rec: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private pending: Token[] = [];
  private seq = 0;
  private lastEnd = 0;
  private stopped = false;
  private retry: ReturnType<typeof setTimeout> | null = null;
  /** Words Soniox has heard but not settled yet (it sends them again, revised, until they're final). */
  private interim: Token[] = [];
  private live: { send(seq: number, text: string): void; stop(): void } | null = null;
  private sentText = '';
  private sendTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSend = 0;

  private cur: Sentence;

  constructor(private api: NonNullable<DeviceApi['listen']>, private onStatus: (s: ListenStatus, detail?: string) => void, firstSeq = 0, broadcastId?: string) {
    this.seq = firstSeq;
    this.cur = this.sentence();
    if (broadcastId) this.live = api.partial(broadcastId);
  }

  async start() {
    this.onStatus('starting');
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error('This screen has no way to use a microphone.'), { name: 'NotAllowedError' });
      const ask = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true, channelCount: 1 } });
      // A screen that never answers (no microphone support, or a question nobody has seen) says so instead of
      // waiting for ever; if it's allowed later, listening starts then.
      const slow = setTimeout(() => { if (!this.stream && !this.stopped) this.onStatus('mic-blocked', 'no answer'); }, 15_000);
      try { this.stream = await ask; } finally { clearTimeout(slow); }
      if (this.stopped) { this.stream.getTracks().forEach((t) => t.stop()); return; }
    } catch (e) {
      const name = (e as { name?: string }).name;
      this.onStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'mic-blocked' : 'no-mic', e instanceof Error ? e.message : undefined);
      // Try again in a minute (a microphone may be plugged in, or allowed).
      if (!this.stopped) this.retry = setTimeout(() => void this.start(), 60_000);
      return;
    }
    await this.connect();
  }

  private async connect() {
    if (this.stopped) return;
    let key: { apiKey: string; model: string };
    try { key = await this.api.token(); } catch (e) { this.onStatus('offline', e instanceof Error ? e.message : undefined); this.again(); return; }
    let ws: WebSocket;
    // A page that isn't allowed to reach the speech-to-text service throws here: say so and try again.
    try { ws = new WebSocket(URL_WS); } catch (e) { this.onStatus('offline', e instanceof Error ? e.message : undefined); this.again(); return; }
    ws.binaryType = 'arraybuffer';
    // Soniox's times count from the start of each connection.
    this.lastEnd = 0;
    this.ws = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({
        api_key: key.apiKey, model: key.model, audio_format: 'auto',
        language_hints: ['ar', 'en', 'ur'], enable_endpoint_detection: true,
        context: { general: [{ key: 'domain', value: 'Islamic' }, { key: 'topic', value: 'Friday khutbah (sermon) in a masjid' }] },
      }));
      this.record();
      this.onStatus('listening');
    };
    ws.onmessage = (ev) => { try { this.heard(JSON.parse(String(ev.data))); } catch { /* not JSON */ } };
    ws.onerror = () => { /* onclose follows */ };
    ws.onclose = () => {
      this.rec?.stop(); this.rec = null;
      if (this.ws === ws) this.ws = null;
      this.flush();
      if (!this.stopped) { this.onStatus('offline'); this.again(); }
    };
  }

  private again() { if (!this.stopped && !this.retry) this.retry = setTimeout(() => { this.retry = null; void this.connect(); }, 3000); }

  private record() {
    if (!this.stream) return;
    const type = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'].find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t));
    const rec = new MediaRecorder(this.stream, type ? { mimeType: type, audioBitsPerSecond: 48_000 } : undefined);
    rec.ondataavailable = async (e) => { if (e.data.size && this.ws?.readyState === WebSocket.OPEN) this.ws.send(await e.data.arrayBuffer()); };
    rec.start(250);
    this.rec = rec;
  }

  private heard(msg: { tokens?: Token[]; error_code?: number; error_message?: string; finished?: boolean }) {
    if (msg.error_code) { console.warn('[khutbah] speech-to-text', msg.error_code, msg.error_message); this.onStatus('offline', msg.error_message); return; }
    const interim: Token[] = [];
    for (const t of msg.tokens ?? []) {
      if (!t.is_final) { if (t.text !== '<end>' && t.text !== '<fin>') interim.push(t); continue; }
      if (t.text === '<end>' || t.text === '<fin>') { this.flush(); continue; }
      this.pending.push(t);
      const n = wordsFrom(this.pending).length;
      if ((n >= MIN_WORDS && /[.!?؟۔]\s*$/.test(t.text)) || n >= MAX_WORDS) this.flush();
    }
    this.interim = interim;
    if (this.pending.length) this.grew();
    this.sendHeard();
  }

  private sentence(): Sentence { return { seq: this.seq + 1, chain: Promise.resolve(), queued: false, sent: 0, finished: false, timer: null }; }

  /** More words settled: the sentence so far goes to be translated (a couple of words at a time). */
  private grew() {
    const s = this.cur;
    const n = wordsFrom(this.pending).length;
    if (n - s.sent >= MORE_WORDS) this.sendSoFar(s);
    else if (n > s.sent && !s.timer) s.timer = setTimeout(() => { s.timer = null; this.sendSoFar(s); }, MORE_WAIT_MS);
  }

  private sendSoFar(s: Sentence) {
    if (s.queued || s.finished || this.stopped) return;
    s.queued = true;
    s.chain = s.chain.then(async () => {
      s.queued = false;
      if (s.finished || this.stopped || s !== this.cur) return;
      const words = wordsFrom(this.pending);
      if (words.length <= s.sent) return;
      s.sent = words.length;
      await this.api.translate({ seq: s.seq, arabic: markUnsure(words), audioSeconds: 0, final: false }).catch((e) => console.warn('[khutbah] couldn’t send the sentence so far', s.seq, e));
      // More was said while that was being translated.
      if (!s.finished && s === this.cur) this.grew();
    });
  }

  /** The sentence so far, to the screens, at most 5 times a second. */
  private sendHeard() {
    if (!this.live || this.sendTimer) return;
    const wait = Math.max(0, 200 - (Date.now() - this.lastSend));
    this.sendTimer = setTimeout(() => {
      this.sendTimer = null;
      const text = wordsFrom([...this.pending, ...this.interim]).map((x) => x.w).join(' ');
      if (!text || text === this.sentText || this.stopped) return;
      this.sentText = text;
      this.lastSend = Date.now();
      this.live?.send(this.seq + 1, text);
    }, wait);
  }

  /** Sends what's been heard since the last sentence, if anything. */
  private flush() {
    const words = wordsFrom(this.pending);
    const end = this.pending.at(-1)?.end_ms ?? this.lastEnd;
    this.pending = [];
    this.sentText = '';
    if (!words.length) return;
    const text = markUnsure(words);
    const seconds = Math.max(0, (end - this.lastEnd) / 1000);
    this.lastEnd = end;
    const s = this.cur;
    s.finished = true;
    if (s.timer) clearTimeout(s.timer);
    const seq = ++this.seq;
    this.cur = this.sentence();
    const send = (tries: number): Promise<void> => this.api.translate({ seq, arabic: text, audioSeconds: seconds, final: true }).catch((e) => {
      if (tries > 0) return new Promise<void>((r) => setTimeout(r, 1500)).then(() => send(tries - 1));
      console.warn('[khutbah] couldn’t send a sentence', seq, e);
    });
    // After the sentence-so-far call in progress, if any.
    s.chain = s.chain.then(() => send(2));
  }

  stop() {
    this.stopped = true;
    if (this.retry) clearTimeout(this.retry);
    try { this.rec?.stop(); } catch { /* already stopped */ }
    this.flush();
    try { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(new ArrayBuffer(0)); } catch { /* closing anyway */ }
    setTimeout(() => this.ws?.close(), 1500);
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.sendTimer) clearTimeout(this.sendTimer);
    this.live?.stop();
    this.onStatus('stopped');
  }
}
